// src/components/demo/DemoLeadForm.js
//
// Lead capture (Nigel 2026-07-23; Claude Design screen J — DLeadCapture):
// the "just curious" walk-up who doesn't ride. STAFF-operated (per design —
// unlike the rider form there's no device hand-over): grab a name and email,
// consent defaults ON (the ask "want to hear more?" is the conversation),
// save → onDemoLeadCreated CF sends the one-time intro email named from the
// recording staffer and syncs Klaviyo if consented.
//
// Deviations from the mock, both deliberate:
//  - First + Last instead of one Name field (Klaviyo profiles want both).
//  - "Recorded by" is the signed-in staffer (shown, not a dropdown — auth
//    already knows who's holding the phone).

import React, { useEffect, useState } from 'react';
import { FScreen, FHeader, FHeadline, FBtn, FLabel } from '../foam/FoamPrimitives';
import {
  isValidEmail, suspiciousTld, nearDuplicateContact, listEventContactEmails,
} from '../../services/demoService';

const Field = ({ label, mono, ...props }) => (
  <div style={{ minWidth: 0 }}>
    <div className="mb-[7px]"><FLabel>{label}</FLabel></div>
    <input
      {...props}
      className={`w-full text-[16px] px-[14px] ${mono ? 'font-mono' : 'font-sans'}`}
      style={{ height: 56, minWidth: 0, maxWidth: '100%', border: '1.5px solid var(--foam-line)', borderRadius: 0, background: 'var(--foam-paper)' }}
    />
  </div>
);

export default function DemoLeadForm({ user, eventId, onSubmit, onBack }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(true);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  // Everyone already captured at this event (riders + leads), so a mistyped
  // lead can be caught against the waiver the same person just signed.
  const [knownContacts, setKnownContacts] = useState([]);
  // Read-back step: shown once, only for an address worth a second look.
  const [reviewing, setReviewing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listEventContactEmails(eventId).then((c) => { if (!cancelled) setKnownContacts(c); });
    return () => { cancelled = true; };
  }, [eventId]);

  const staffName = user?.displayName || (user?.email || '').split('@')[0] || 'the WAKA crew';
  const emailOk = isValidEmail(email);
  const ready = firstName.trim().length > 0 && lastName.trim().length > 0 && emailOk && !busy;

  // Warnings, never blocks (round 2 §2b).
  const oddTld = emailOk ? suspiciousTld(email) : null;
  const nearDupe = emailOk ? nearDuplicateContact(email, knownContacts) : null;
  const worthReading = !!(oddTld || nearDupe);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await onSubmit({ firstName, lastName, email, emailConsent: consent });
      setDone(true);
    } catch (e) {
      setError(e.message || 'Could not save — try again.');
      setReviewing(false);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <FScreen
        header={<FHeader right={<FLabel color="var(--foam-teal)">On the list</FLabel>} />}
        footer={<FBtn primary onClick={onBack}>Back to the rack</FBtn>}
      >
        <FHeadline size={44}>They're<br /><FHeadline.Accent>in.</FHeadline.Accent></FHeadline>
        <div
          className="font-sans text-[14px]"
          style={{ color: 'var(--foam-ink)', lineHeight: 1.55, padding: 16,
            border: '1.5px solid var(--foam-teal)', background: 'var(--foam-lagoon)' }}
        >
          Intro email is on its way to <strong>{email.trim()}</strong> from you
          ({staffName}){consent ? ' and they\u2019re added to the WAKA list' : ''}.
        </div>
      </FScreen>
    );
  }

  // ── Read the address back to the RIDER, not a validation error at the staffer.
  // The staffer typed it; the rider is the only one who knows if it's right.
  if (reviewing) {
    return (
      <FScreen
        header={<FHeader right={<FLabel color="var(--foam-orange)">Check the address</FLabel>} />}
        footer={
          <>
            <FBtn primary disabled={busy} onClick={save}>
              {busy ? 'One sec…' : 'That’s right — send it'}
            </FBtn>
            <FBtn ghost disabled={busy} onClick={() => setReviewing(false)}>Fix the address</FBtn>
          </>
        }
      >
        <FHeadline size={38}>Read this<br /><FHeadline.Accent>back to them.</FHeadline.Accent></FHeadline>

        {/* The address, big enough to read off the phone at arm's length. */}
        <div style={{ border: '1.5px solid var(--foam-carbon)', background: 'var(--foam-paper)', padding: '20px 18px' }}>
          <div
            className="font-mono"
            style={{ fontSize: 22, lineHeight: 1.35, color: 'var(--foam-ink)', wordBreak: 'break-all' }}
          >
            {email.trim().toLowerCase()}
          </div>
        </div>

        {nearDupe && (
          <div style={{ border: '1.5px solid var(--foam-orange)', background: '#FFF7F3', padding: 16 }}>
            <div
              className="font-display font-extrabold uppercase text-[18px]"
              style={{ color: 'var(--foam-orange)', letterSpacing: '-0.01em' }}
            >
              Did you mean…?
            </div>
            <p className="font-sans text-[14px] mt-[6px]" style={{ color: 'var(--foam-ink)', lineHeight: 1.5 }}>
              One letter off {nearDupe.source === 'rider' ? 'a rider who signed a waiver' : 'another lead taken'}
              {nearDupe.name ? ` today — ${nearDupe.name}` : ' today'}:
            </p>
            <button
              type="button"
              onClick={() => { setEmail(nearDupe.email); setReviewing(false); }}
              className="font-mono text-[16px] underline text-left mt-[8px]"
              style={{ color: 'var(--foam-orange)', minHeight: 44, wordBreak: 'break-all' }}
            >
              Use {nearDupe.email} instead →
            </button>
          </div>
        )}

        {oddTld && (
          <div style={{ border: '1.5px solid var(--foam-orange)', background: '#FFF7F3', padding: 16 }}>
            <div
              className="font-display font-extrabold uppercase text-[18px]"
              style={{ color: 'var(--foam-orange)', letterSpacing: '-0.01em' }}
            >
              .{oddTld} — unusual ending
            </div>
            <p className="font-sans text-[14px] mt-[6px]" style={{ color: 'var(--foam-ink)', lineHeight: 1.5 }}>
              Might be a typo for <strong>.com</strong>. If <strong>.{oddTld}</strong> really is their
              address, send it — this is a warning, not a block.
            </p>
          </div>
        )}

        <p className="font-sans text-[12.5px]" style={{ color: 'var(--foam-ink-2)', lineHeight: 1.5 }}>
          A wrong address here is invisible afterwards — the intro email reports as sent either way, and
          lead records carry no phone number to fall back on.
        </p>

        {error && <FLabel color="var(--foam-orange)">{error}</FLabel>}
      </FScreen>
    );
  }

  return (
    <FScreen
      header={<FHeader right={<FLabel color="var(--foam-orange)">● Just curious</FLabel>} />}
      footer={
        <>
          <FBtn
            primary
            disabled={!ready}
            onClick={() => {
              // One read-back for an address worth a second look, then straight
              // through. A clean address never sees this step.
              if (worthReading) { setError(null); setReviewing(true); return; }
              save();
            }}
          >
            {busy ? 'One sec…' : worthReading ? 'Check the address →' : 'Add to list & send intro →'}
          </FBtn>
          <FBtn ghost disabled={busy} onClick={onBack}>Back</FBtn>
        </>
      }
    >
      <FHeadline size={40}>Add to<br /><FHeadline.Accent>the list</FHeadline.Accent></FHeadline>
      <p className="font-sans text-[14px]" style={{ color: 'var(--foam-ink-2)', lineHeight: 1.55 }}>
        Not riding today but want to hear more? Grab a name and email — we'll send a friendly
        intro and add them to the WAKA list.
      </p>

      <div className="flex flex-col gap-[12px]">
        <div className="flex gap-[12px]">
          <div className="flex-1" style={{ minWidth: 0 }}>
            <Field label="First" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </div>
          <div className="flex-1" style={{ minWidth: 0 }}>
            <Field label="Last" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
        </div>
        <Field label="Email" mono type="email" inputMode="email" value={email}
          onChange={(e) => setEmail(e.target.value)} onBlur={() => setTouched(true)} />
        {touched && email && !emailOk && (
          <FLabel color="var(--foam-orange)">Check that email — it doesn't look right</FLabel>
        )}
        {touched && emailOk && nearDupe && (
          <FLabel color="var(--foam-orange)">
            One letter off {nearDupe.name || nearDupe.email} — we'll read it back
          </FLabel>
        )}
        {touched && emailOk && !nearDupe && oddTld && (
          <FLabel color="var(--foam-orange)">.{oddTld} is unusual — we'll read it back</FLabel>
        )}
        <div>
          <div className="mb-[7px]"><FLabel>Recorded by</FLabel></div>
          <div className="font-sans text-[16px] px-[14px] flex items-center"
            style={{ height: 56, border: '1.5px solid var(--foam-hair)', background: 'var(--foam-paper-2)', color: 'var(--foam-ink-2)' }}>
            {staffName}
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setConsent(!consent)}
        className="flex gap-[12px] items-start text-left w-full"
        style={{ minHeight: 44, padding: 15, border: '1.5px solid var(--foam-line)', background: 'var(--foam-paper-2)' }}
      >
        <span
          className="flex-shrink-0 flex items-center justify-center"
          style={{ width: 22, height: 22, marginTop: 1,
            background: consent ? 'var(--foam-carbon)' : 'var(--foam-paper)',
            border: '1.5px solid var(--foam-line)',
            color: 'var(--foam-paper)', fontWeight: 700, fontSize: 13 }}
        >
          {consent ? '✓' : ''}
        </span>
        <span className="font-sans text-[13.5px]" style={{ color: 'var(--foam-ink)', lineHeight: 1.5 }}>
          They're happy to hear from WAKA by email.{' '}
          <span style={{ color: 'var(--foam-ink-2)' }}>Added to Klaviyo on save.</span>
        </span>
      </button>

      <div className="flex gap-[9px] items-start"
        style={{ background: 'var(--foam-lagoon)', border: '1px solid var(--foam-teal-bright)', padding: '12px 13px' }}>
        <span style={{ color: 'var(--foam-teal)', fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>✦</span>
        <div className="font-sans text-[12.5px]" style={{ color: 'var(--foam-teal)', lineHeight: 1.5 }}>
          Saving sends a one-time intro email from <strong>{staffName}</strong> with a link to
          waka.surf — then they're in the flow like everyone else.
        </div>
      </div>

      {error && <FLabel color="var(--foam-orange)">{error}</FLabel>}
    </FScreen>
  );
}
